import { useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../lib/components/custom/Icon";
import { Button } from "../../lib/components/ui/button";
import { Card } from "../../lib/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "../../lib/components/ui/dialog";
import { Input } from "../../lib/components/ui/input";
import { Label } from "../../lib/components/ui/label";
import { Separator } from "../../lib/components/ui/separator";
import { useApi } from "../../lib/hooks/api/use-api";

export default function AuthSection() {
	const { t } = useTranslation();
	const { auth } = useApi();
	const { data: session } = auth.useSession();
	const [changePasswordDialog, setChangePasswordDialog] = useState(false);
	const [changePasswordData, setChangePasswordData] = useState({
		currentPassword: "",
		newPassword: "",
		confirmPassword: "",
	});
	const [changePasswordError, setChangePasswordError] = useState("");
	const [changePasswordLoading, setChangePasswordLoading] = useState(false);
	const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
	const [resetPasswordError, setResetPasswordError] = useState("");

	const handleChangePasswordInput = (
		e: React.ChangeEvent<HTMLInputElement>,
	) => {
		const { name, value } = e.target;
		setChangePasswordData((prev) => ({ ...prev, [name]: value }));
		if (changePasswordError) setChangePasswordError("");
	};

	const handleChangePassword = async (e: React.FormEvent) => {
		e.preventDefault();
		setChangePasswordError("");
		setChangePasswordLoading(true);

		// Validation
		if (!changePasswordData.currentPassword.trim()) {
			setChangePasswordError(
				t("settings.auth.errors.current_password_required"),
			);
			setChangePasswordLoading(false);
			return;
		}

		if (changePasswordData.newPassword.length < 6) {
			setChangePasswordError(t("settings.auth.errors.new_password_min"));
			setChangePasswordLoading(false);
			return;
		}

		if (changePasswordData.newPassword !== changePasswordData.confirmPassword) {
			setChangePasswordError(t("settings.auth.errors.passwords_dont_match"));
			setChangePasswordLoading(false);
			return;
		}

		try {
			const response = await auth.changePassword({
				newPassword: changePasswordData.newPassword,
				currentPassword: changePasswordData.currentPassword,
			});

			if (response.error) {
				setChangePasswordError(
					response.error.message || t("settings.auth.errors.change_failed"),
				);
			} else {
				setChangePasswordDialog(false);
				setChangePasswordData({
					currentPassword: "",
					newPassword: "",
					confirmPassword: "",
				});
				// You could add a success toast here
			}
		} catch (err) {
			setChangePasswordError(t("settings.auth.errors.unexpected"));
			console.error("Change password error:", err);
		} finally {
			setChangePasswordLoading(false);
		}
	};

	const handleResetPassword = async () => {
		setResetPasswordError("");
		setResetPasswordLoading(true);

		try {
			const response = await auth.requestPasswordReset({
				email: session?.user?.email || "",
				redirectTo: "/auth/reset-password",
			});

			if (response.error) {
				setResetPasswordError(
					response.error.message || t("settings.auth.errors.reset_failed"),
				);
			} else {
				// You could add a success toast here
				alert(t("settings.auth.reset_password.success"));
			}
		} catch (err) {
			setResetPasswordError(t("settings.auth.errors.unexpected"));
			console.error("Reset password error:", err);
		} finally {
			setResetPasswordLoading(false);
		}
	};

	return (
		<Card className="p-6">
			<h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
				<Icon name="Key" className="w-5 h-5" />
				{t("settings.auth.header")}
			</h2>

			<div className="py-4">
				<Label className="text-sm font-medium">
					{t("settings.auth.reset_password.label")}
				</Label>
				<p className="text-sm text-muted-foreground mt-1 mb-3">
					{t("settings.auth.reset_password.description")}
				</p>
				{resetPasswordError && (
					<div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-3 mb-3">
						{resetPasswordError}
					</div>
				)}
				<Button
					variant="outline"
					size="sm"
					onClick={handleResetPassword}
					disabled={resetPasswordLoading || !session?.user?.email}
				>
					{resetPasswordLoading ? (
						<>
							<Icon name="Loader" className="w-4 h-4 mr-2 animate-spin" />
							{t("settings.auth.reset_password.sending")}
						</>
					) : (
						<>
							<Icon name="Mail" className="w-4 h-4 mr-2" />
							{t("settings.auth.reset_password.button")}
						</>
					)}
				</Button>
			</div>

			<Separator />

			<div className="space-y-1">
				<div className="py-4">
					<Label className="text-sm font-medium">
						{t("settings.auth.change_password.label")}
					</Label>
					<p className="text-sm text-muted-foreground mt-1 mb-3">
						{t("settings.auth.change_password.description")}
					</p>
					<Dialog
						open={changePasswordDialog}
						onOpenChange={setChangePasswordDialog}
					>
						<DialogTrigger asChild>
							<Button variant="outline" size="sm">
								<Icon name="Lock" className="w-4 h-4 mr-2" />
								{t("settings.auth.change_password.button")}
							</Button>
						</DialogTrigger>
						<DialogContent className="sm:max-w-[425px]">
							<DialogHeader>
								<DialogTitle>
									{t("settings.auth.change_password.dialog_title")}
								</DialogTitle>
							</DialogHeader>
							<form onSubmit={handleChangePassword} className="space-y-4">
								<div>
									<Label htmlFor="currentPassword">
										{t("settings.auth.change_password.current_password_label")}
									</Label>
									<Input
										id="currentPassword"
										name="currentPassword"
										type="password"
										placeholder={t(
											"settings.auth.change_password.current_password_placeholder",
										)}
										value={changePasswordData.currentPassword}
										onChange={handleChangePasswordInput}
										required
									/>
								</div>
								<div>
									<Label htmlFor="newPassword">
										{t("settings.auth.change_password.new_password_label")}
									</Label>
									<Input
										id="newPassword"
										name="newPassword"
										type="password"
										placeholder={t(
											"settings.auth.change_password.new_password_placeholder",
										)}
										value={changePasswordData.newPassword}
										onChange={handleChangePasswordInput}
										required
									/>
								</div>
								<div>
									<Label htmlFor="confirmPassword">
										{t("settings.auth.change_password.confirm_password_label")}
									</Label>
									<Input
										id="confirmPassword"
										name="confirmPassword"
										type="password"
										placeholder={t(
											"settings.auth.change_password.confirm_password_placeholder",
										)}
										value={changePasswordData.confirmPassword}
										onChange={handleChangePasswordInput}
										required
									/>
								</div>
								{changePasswordError && (
									<div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-3">
										{changePasswordError}
									</div>
								)}
								<Button
									type="submit"
									className="w-full"
									disabled={changePasswordLoading}
								>
									{changePasswordLoading ? (
										<>
											<Icon
												name="Loader"
												className="w-4 h-4 mr-2 animate-spin"
											/>
											{t("settings.auth.change_password.submitting")}
										</>
									) : (
										<>
											<Icon name="Lock" className="w-4 h-4 mr-2" />
											{t("settings.auth.change_password.submit")}
										</>
									)}
								</Button>
							</form>
						</DialogContent>
					</Dialog>
				</div>

				<Separator />

				<div className="flex items-center justify-between py-4">
					<div className="flex-1">
						<Label className="text-sm font-medium">
							{t("settings.auth.email_verification.label")}
						</Label>
						<p className="text-sm text-muted-foreground mt-1">
							{t("settings.auth.email_verification.description")}
						</p>
					</div>
					<div className="ml-4">
						{session?.user?.emailVerified ? (
							<div className="flex items-center gap-2">
								<Icon name="Check" className="w-4 h-4 text-green-500" />
								<span className="text-sm text-green-500">
									{t("settings.auth.email_verification.verified")}
								</span>
							</div>
						) : (
							<div className="flex items-center gap-2">
								<Icon name="X" className="w-4 h-4 text-destructive" />
								<span className="text-sm text-destructive">
									{t("settings.auth.email_verification.unverified")}
								</span>
							</div>
						)}
					</div>
				</div>
			</div>
		</Card>
	);
}
